import { TAILWIND_TO_CSS } from "utils/constant/STYLE";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useRef, useEffect, useState } from "react";
import { TYPO } from "styles/Typo";
import moment from "moment-timezone";
import Button from "components/common/buttons/buttons";

// 누적 로그수익률 계산 함수
const calculateCumulativeLogReturn = (logReturnData: any[]) => {
  if (!logReturnData || logReturnData.length === 0) return [];

  let cumulativeSum = 0;
  return logReturnData.map((item: any) => {
    cumulativeSum += item.DAILY_CHG_LOG || 0;
    return {
      ...item,
      CUMULATIVE_LOG_RETURN: cumulativeSum,
    };
  });
};

// 10일 이동평균 계산 함수
const calculateMovingAverage = (data: any[], windowSize: number = 10) => {
  if (!data || data.length === 0) return [];

  return data.map((item: any, index: number) => {
    const startIndex = Math.max(0, index - windowSize + 1);
    const window = data.slice(startIndex, index + 1);
    const sum = window.reduce(
      (acc, curr) => acc + (curr.CUMULATIVE_LOG_RETURN || 0),
      0
    );
    const average = sum / window.length;

    return {
      ...item,
      MOVING_AVERAGE_10D: average,
    };
  });
};

const omissionSereisData = (data: any) => {
  return data;
  if (!data) return [];
  const omissionSereisData = data.filter((point: any, index: any) => {
    if (index === 0) return true;
    if (index === data.length - 1) return true;
    if (index % 7 === 0) return null;
    // if (index % 5 === 0) return null;
    // if (index % 3 === 0) return null;
    // if (index % 11 === 0) return null;
    // if (index % 13 === 0) return null;
    // if (index % 17 === 0) return null;
    // if (index % 19 === 0) return null;
    // if (index % 23 === 0) return null;
    // if (index % 29 === 0) return null;
    // if (index % 31 === 0) return null;
    // if (index % 37 === 0) return null;
    // if (index % 41 === 0) return null;
    // if (index % 43 === 0) return null;
    // if (index % 47 === 0) return null;
    // if (index % 53 === 0) return null;
    // if (index % 59 === 0) return null;
    // if (index % 61 === 0) return null;
    // if (index % 67 === 0) return null;

    if (index % 2 === 1) {
      return true;
    } else {
      return null;
    }
  });
  return omissionSereisData;
};

function PortfolioTeaserSimLineChart({
  data,
  activeIndex,
  theme = "white",
  height,
  paddingTop = 0,
  paddingBottom = 0,
}: {
  data: any;
  activeIndex: number;
  theme?: "white" | "dark";
  height?: number;
  paddingTop?: number;
  paddingBottom?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const pulseAnimationRef = useRef<any>(null);
  const activeRingsRef = useRef<any[]>([]); // 현재 활성화된 링들 추적
  const isAnimatingRef = useRef<boolean>(false); // 애니메이션 실행 상태
  const activeMarkerRef = useRef<any>(null); // 현재 활성 마커 추적
  const [chartHeight, setChartHeight] = useState<number | undefined>(
    height || undefined
  );
  const [chartWidth, setChartWidth] = useState<number | undefined>(undefined);
  const [count, setCount] = useState<number>(126);

  // 기존 링들 모두 제거 함수
  const clearAllRings = () => {
    activeRingsRef.current.forEach((ring) => {
      if (ring && ring.destroy) {
        ring.destroy();
      }
    });
    activeRingsRef.current = [];
  };

  // 활성 마커 업데이트 함수
  const updateActiveMarker = (chart: any, currentActiveIndex: number) => {
    // 기존 마커 제거
    if (activeMarkerRef.current && activeMarkerRef.current.destroy) {
      activeMarkerRef.current.destroy();
      activeMarkerRef.current = null;
    }

    if (!chart || !chart.series || !chart.series[currentActiveIndex]) return;

    const currentSeries = chart.series[currentActiveIndex];
    if (!currentSeries.points || currentSeries.points.length === 0) return;

    const lastPoint = currentSeries.points[currentSeries.points.length - 1];
    if (!lastPoint) return;

    const x = chart.plotLeft + (lastPoint.plotX || 0);
    const y = chart.plotTop + (lastPoint.plotY || 0);

    // 새 마커 생성
    activeMarkerRef.current = chart.renderer
      .circle(x, y, 6)
      .attr({
        fill: "var(--risk-green)",
        stroke: "white",
        "stroke-width": 0,
        zIndex: 8, // 맥동 링보다 위에 표시
      })
      .add();
  };

  // 맥동 링 생성 함수
  const createPulseRing = (chart: any, x: number, y: number) => {
    if (!chart || !chart.renderer) return;

    const ring = chart.renderer
      .circle(x, y, 4)
      .attr({ fill: "var(--risk-green)", opacity: 0.5, zIndex: 7 })
      .add();

    // 생성된 링을 추적 배열에 추가
    activeRingsRef.current.push(ring);

    ring.animate(
      { r: 20, opacity: 0 },
      {
        duration: 1500,
        easing: "linear",
        complete: function () {
          // 애니메이션 완료 시 배열에서 제거 후 파괴
          const index = activeRingsRef.current.indexOf(ring);
          if (index > -1) {
            activeRingsRef.current.splice(index, 1);
          }
          ring.destroy();
        },
      }
    );
  };

  // 맥동 애니메이션 시작/재시작 함수
  const startPulseAnimation = (chart: any, currentActiveIndex: number) => {
    // 중복 실행 방지
    if (isAnimatingRef.current) {
      return;
    }

    isAnimatingRef.current = true;

    // 기존 애니메이션과 링들 모두 정리
    if (pulseAnimationRef.current) {
      clearInterval(pulseAnimationRef.current);
      pulseAnimationRef.current = null;
    }
    clearAllRings();

    if (!chart || !chart.series || !chart.series[currentActiveIndex]) {
      isAnimatingRef.current = false;
      return;
    }

    const currentSeries = chart.series[currentActiveIndex];
    if (!currentSeries.points || currentSeries.points.length === 0) {
      isAnimatingRef.current = false;
      return;
    }

    const point = currentSeries.points[currentSeries.points.length - 1];
    if (!point) {
      isAnimatingRef.current = false;
      return;
    }

    // 약간의 지연 후 애니메이션 시작 (기존 링들이 완전히 정리되도록)
    setTimeout(() => {
      // 마커 위치도 다시 확인하여 동기화
      updateActiveMarker(chart, currentActiveIndex);

      const refreshedPoint =
        chart.series[currentActiveIndex]?.points?.[
          chart.series[currentActiveIndex].points.length - 1
        ];
      if (!refreshedPoint) {
        isAnimatingRef.current = false;
        return;
      }

      const x = chart.plotLeft + (refreshedPoint.plotX || 0);
      const y = chart.plotTop + (refreshedPoint.plotY || 0);

      // 첫 번째 맥동 실행
      createPulseRing(chart, x, y);

      // 1.5초마다 반복 실행
      pulseAnimationRef.current = setInterval(() => {
        // 차트와 포인트가 여전히 유효한지 확인
        if (
          chart &&
          chart.series &&
          chart.series[currentActiveIndex] &&
          chart.series[currentActiveIndex].points &&
          chart.series[currentActiveIndex].points.length > 0
        ) {
          const currentPoint =
            chart.series[currentActiveIndex].points[
              chart.series[currentActiveIndex].points.length - 1
            ];
          if (currentPoint) {
            const currentX = chart.plotLeft + (currentPoint.plotX || 0);
            const currentY = chart.plotTop + (currentPoint.plotY || 0);
            createPulseRing(chart, currentX, currentY);
          }
        }
      }, 1500);

      isAnimatingRef.current = false;
    }, 100); // 100ms 지연
  };

  useEffect(() => {
    // 초기 차트 너비 설정 (마운트 시)
    const updateChartWidth = () => {
      setChartWidth(Math.min(window.innerWidth, 800));
    };

    // 컴포넌트 마운트 시 즉시 실행
    updateChartWidth();

    // resize 이벤트 리스너 등록
    window.addEventListener("resize", updateChartWidth);

    return () => {
      window.removeEventListener("resize", updateChartWidth);
    };
  }, []);

  // activeIndex 변경 시 마커와 애니메이션 즉시 업데이트
  // useEffect(() => {
  //   if (chartRef.current) {
  //     updateActiveMarker(chartRef.current, activeIndex);
  //     startPulseAnimation(chartRef.current, activeIndex);
  //   }
  // }, [activeIndex]);

  // 컴포넌트 언마운트 시 애니메이션과 마커 정리
  useEffect(() => {
    return () => {
      if (pulseAnimationRef.current) {
        clearInterval(pulseAnimationRef.current);
        pulseAnimationRef.current = null;
      }
      clearAllRings(); // 모든 링들도 정리
      if (activeMarkerRef.current && activeMarkerRef.current.destroy) {
        activeMarkerRef.current.destroy(); // 활성 마커도 정리
        activeMarkerRef.current = null;
      }
      isAnimatingRef.current = false;
    };
  }, []);

  // 135개로 변경 (10일 이평선 계산을 위해 +9개)
  const slicedData = data.map((item: any) => {
    return {
      ...item,
      LOG_RETURN: item.LOG_RETURN.slice(-count + 9),
    };
  });

  // 첫 번째 종목 기준으로 x축 데이터 생성 (누적 로그수익률 + 10일 이평선 계산 후 마지막 126개)
  const firstItemProcessedData = slicedData?.[0]?.LOG_RETURN
    ? (() => {
        const cumulativeData = calculateCumulativeLogReturn(
          slicedData[0].LOG_RETURN
        );
        const movingAverageData = calculateMovingAverage(cumulativeData, 10);
        return movingAverageData.slice(-count); // 마지막 126개만 사용
      })()
    : [];

  const omissionXaxisData = omissionSereisData(firstItemProcessedData)?.map(
    (item: any) => item?.BASE_DT
  );

  const allSeriesData = slicedData.map((item: any, index: number) => {
    // 각 종목별로 누적 로그수익률 계산
    const cumulativeData = calculateCumulativeLogReturn(item?.LOG_RETURN || []);

    // 10일 이동평균 계산
    const movingAverageData = calculateMovingAverage(cumulativeData, 10);
    // 마지막 126개만 사용
    const finalData = movingAverageData.slice(-count);

    // 데이터 포인트를 숫자 배열로 단순화 (마커는 별도 관리)
    const seriesData = finalData?.map(
      (dataItem: any) => dataItem?.MOVING_AVERAGE_10D
    );

    // 활성 시리즈의 색상 결정 (첫 번째 vs 마지막 데이터 비교)
    let activeColor = "var(--risk-green)"; // 기본값
    let activeMarkerColor = "rgba(34, 197, 94, 0.3)"; // 기본 마커 색상 (green with opacity)
    if (index === activeIndex && seriesData.length > 0) {
      const firstValue = seriesData[0];
      const lastValue = seriesData[seriesData.length - 1];
      if (lastValue < firstValue) {
        activeColor = "var(--blue50)"; // 상승 시 파란색
        activeMarkerColor = "rgba(49, 130, 246, 0.3)"; // #3182f6 with 0.3 opacity
      } else {
        activeColor = "var(--red50)"; // 하락 시 빨간색
        activeMarkerColor = "rgba(246, 101, 112, 0.3)"; // #f66570 with 0.3 opacity
      }
    }

    // 활성 시리즈의 마지막 포인트에만 마커 표시
    const lastIdx = seriesData.length - 1;
    const seriesDataWithLastMarker = seriesData.map((y: number, i: number) =>
      i === lastIdx && index === activeIndex
        ? {
            y,
            marker: {
              enabled: true,
              symbol: "circle",
              radius: 4, // 중심 원 반지름 4 (지름 8)
              lineWidth: 18, // 테두리 두께 10 (양쪽으로 10씩 = 총 20 추가)
              fillColor: activeColor, // 중심 원은 진한 색상
              lineColor: activeMarkerColor, // 테두리는 투명한 색상
              states: { hover: { enabled: false } },
            },
          }
        : y
    );

    return {
      type: "line",
      name: item?.ITEM_KR_NM,
      data: [...seriesDataWithLastMarker],
      marker: {
        enabled: false,
        symbol: "circle", // 기본 마커 모양도 원으로 통일
        states: {
          hover: {
            enabled: false,
          },
        },
      },

      color: index === activeIndex ? activeColor : "var(--gray20)",

      lineWidth: 3.5,
      zIndex: index === activeIndex ? 3 : 1,
    };
  });

  const options: Highcharts.Options = {
    series: [...allSeriesData],
    xAxis: {
      maxPadding: 0.001,
      categories: omissionXaxisData,
      type: "linear", // x축을 순차적으로 표시
      //   visible: false,
      tickLength: 0, // x축 tick 숨기기
      lineWidth: 0, // x축 선 숨기기
      labels: {
        enabled: false,
      },
    },
    yAxis: {
      //   max: maxValue + 3, // 계산한 최대값을 설정
      //   min: minValue - 3, // 계산한 최소값을 설정
      startOnTick: false, // y축의 시작점을 정확한 min 값으로 설정
      endOnTick: false, // y축의 끝점을 정확한 max 값으로 설정
      gridLineWidth: 0,
      title: {
        text: "",
      },

      tickAmount: 5, // y축 눈금 개수
      opposite: true,
      labels: {
        enabled: false,
      },
    },
    chart: {
      type: "line",
      spacingRight: 0,
      spacingBottom: 0,
      spacingTop: 0,
      spacingLeft: 0,
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 60,
      height: height, // 동적으로 측정된 높이 사용
      backgroundColor: "transparent",
      width: chartWidth,
      // events: {
      //   load: function () {
      //     const chart: any = this;
      //     chartRef.current = chart; // 차트 인스턴스 저장

      //     // 초기 마커와 애니메이션 시작
      //     updateActiveMarker(chart, activeIndex);
      //     startPulseAnimation(chart, activeIndex);
      //   },
      //   redraw: function () {
      //     const chart: any = this;
      //     // 차트가 다시 그려질 때 마커와 링들 정리 후 재생성
      //     clearAllRings();
      //     if (chartRef.current) {
      //       updateActiveMarker(chart, activeIndex);
      //     }
      //   },
      // },
    },
    plotOptions: {
      series: {
        enableMouseTracking: false, // 마우스/터치 이벤트 비활성화
        marker: {
          enabled: false, // 항상 마커(꼬리)를 보이게 설정
          states: {
            hover: {
              enabled: false, // Hover 시 상태 변경 비활성화
            },
          },
        },
        dataGrouping: {
          enabled: false, // 데이터 그룹화 비활성화
        },
        states: {
          inactive: {
            opacity: 1,
          },
          hover: {
            enabled: false,
          },
        },
      },
      line: {
        marker: {
          enabled: false,
          symbol: "circle", // plotOptions에서도 원 모양으로 통일
        },
      },
    },
    tooltip: {
      enabled: false,
    },
    title: {
      text: undefined, // 타이틀 제거
    },
    legend: {
      enabled: false,
    },
    exporting: {
      enabled: false, // 내보내기 버튼(햄버거 모양 아이콘) 비활성화
    },
    credits: {
      enabled: false,
    },
    // responsive: {
    //   rules: [
    //     {
    //       condition: {
    //         maxWidth: 500,
    //       },
    //       chartOptions: {
    //         chart: {
    //           height: null,
    //         },
    //       },
    //     },
    //   ],
    // },
  };
  if (slicedData?.length === 0) return <div></div>;
  return (
    <div>
      {/* <Button
        type="secondary"
        size="sm"
        text={`${count}일`}
        clickHandler={() => {
          if (count === 126) setCount(252);
          else setCount(126);
        }}
      /> */}
      <div
        ref={containerRef}
        className="grow flex flex-col h-full overflow-hidden touch-none"
        style={{
          paddingTop: paddingTop,
          paddingBottom: paddingBottom,
          pointerEvents: "none",
        }}
      >
        <div></div>
        <div className="h-full w-full flex items-center justify-center">
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            style={{ width: "100%" }}
          />
        </div>

        <div className="px-4 py-[10px] flex items-center justify-between w-full">
          <p className={`${TYPO.TYPO9_SB} text-gray60 `}>
            {moment(slicedData?.[activeIndex]?.LOG_RETURN?.[0]?.BASE_DT).format(
              theme === "white" ? "YY년 M월" : "YY.M"
            )}
          </p>
          <p
            className={`${TYPO.TYPO9_MD} text-gray70  bg-gray10 px-2 py-1 rounded-[6px] max-w-[120px] truncate whitespace-nowrap`}
          >
            {`${slicedData?.[activeIndex]?.ITEM_KR_NM}`}
          </p>
          <p className={`${TYPO.TYPO9_SB}  text-gray60`}>
            {moment(
              slicedData?.[activeIndex]?.LOG_RETURN?.[
                slicedData?.[activeIndex]?.LOG_RETURN?.length - 1
              ]?.BASE_DT
            ).format(theme === "white" ? "YY년 M월" : "YY.M")}
          </p>
        </div>
      </div>
    </div>
  );
}

export default PortfolioTeaserSimLineChart;
